import { debounce } from 'utils'

import { constant } from './feedback-messenger-window-draft.constant'
import { applyEditorContent } from './feedback-messenger-window-draft.editor-bridge'
import {
	DraftScope,
	DraftType,
	MessengerDraft,
	consumeIntent,
	loadDraft,
	pruneDrafts,
	removeDraft,
	saveDraft,
} from './feedback-messenger-window-draft.storage'

import { FlashMessage } from '@/component/module/flashMessage/flashMessage'

let prunedThisPageLoad = false

const getWindowForm = () => document.querySelector<HTMLFormElement>(constant.selector.form)

const getFormScope = (form: HTMLFormElement): DraftScope | null => {
	const scope = form.getAttribute(constant.selector.scopeAttribute)
	const type = form.getAttribute(constant.selector.typeAttribute)
	if (scope === null || (type !== 'email' && type !== 'sms')) {
		return null
	}
	return { scope, type: type as DraftType }
}

const saveWindowDraft = () => {
	const form = getWindowForm()
	if (form === null) {
		return
	}
	const scope = getFormScope(form)
	if (scope === null) {
		return
	}
	const subjectInput = form.querySelector<HTMLInputElement>(constant.selector.subjectInput)
	const contentTextarea = form.querySelector<HTMLTextAreaElement>(constant.selector.contentTextarea)
	const templateIdRaw = form.getAttribute(constant.selector.templateIdAttribute)
	const templateId = templateIdRaw !== null && templateIdRaw !== '' ? Number(templateIdRaw) : Number.NaN
	const content = contentTextarea !== null ? contentTextarea.value : ''
	if (content === '') {
		return
	}
	saveDraft(scope, {
		subject: subjectInput !== null ? subjectInput.value : null,
		content,
		templateId: Number.isFinite(templateId) ? templateId : null,
		savedAt: Date.now(),
	})
}

const debouncedSaveWindowDraft = debounce(saveWindowDraft, constant.saveDebounceMs)

const applyDraftToForm = (form: HTMLFormElement, draft: MessengerDraft) => {
	const contentTextarea = form.querySelector<HTMLTextAreaElement>(constant.selector.contentTextarea)
	if (contentTextarea !== null) {
		contentTextarea.value = draft.content
		// When Jodit is already initialized (banner path), the textarea alone is
		// not enough — push the content into the live editor as well.
		applyEditorContent(draft.content)
	}

	const subjectInput = form.querySelector<HTMLInputElement>(constant.selector.subjectInput)
	if (subjectInput !== null && draft.subject !== null && !subjectInput.readOnly) {
		const requiredPart = form.getAttribute(constant.selector.requiredSubjectPartAttribute)
		subjectInput.value =
			requiredPart !== null && !draft.subject.startsWith(requiredPart)
				? `${requiredPart} ${draft.subject}`
				: draft.subject
	}
}

const showDraftBanner = (form: HTMLFormElement, draft: MessengerDraft) => {
	const banner = document.querySelector<HTMLElement>(`[${constant.selector.banner}]`)
	if (banner === null) {
		// Template without the banner markup — fall back to restoring directly.
		applyDraftToForm(form, draft)
		return
	}

	const continueButton = banner.querySelector<HTMLButtonElement>(`[${constant.selector.bannerContinue}]`)
	const freshButton = banner.querySelector<HTMLButtonElement>(`[${constant.selector.bannerFresh}]`)

	banner.hidden = false

	continueButton?.addEventListener('click', () => {
		applyDraftToForm(form, draft)
		banner.hidden = true
	})
	freshButton?.addEventListener('click', () => {
		banner.hidden = true
	})
}

const restoreWindowDraft = () => {
	const form = getWindowForm()
	if (form === null || form.getAttribute(constant.selector.restoredMarkerAttribute) !== null) {
		return
	}
	form.setAttribute(constant.selector.restoredMarkerAttribute, 'true')

	const scope = getFormScope(form)
	if (scope === null) {
		return
	}

	const intent = consumeIntent(scope)
	if (intent === 'fresh') {
		return
	}

	const draft = loadDraft(scope)
	if (draft === null) {
		return
	}

	if (intent === 'restore') {
		applyDraftToForm(form, draft)
		const restoredMessage = form.getAttribute(constant.selector.restoredMessageAttribute)
		if (restoredMessage !== null) {
			FlashMessage.show(restoredMessage)
		}
		return
	}

	// The window opened without an explicit choice (e.g. the reply action) —
	// let the user decide instead of silently overwriting the template.
	showDraftBanner(form, draft)
}

const bindWindowDraft = () => {
	const form = getWindowForm()
	if (form === null || form.getAttribute(constant.selector.boundMarkerAttribute) !== null) {
		return
	}
	form.setAttribute(constant.selector.boundMarkerAttribute, 'true')

	form.addEventListener('input', () => debouncedSaveWindowDraft())
	form.addEventListener('change', () => debouncedSaveWindowDraft())
	form.addEventListener('submit', () => {
		const nette = (
			window as unknown as { Nette?: { validateForm: (sender: HTMLFormElement, onlyCheck?: boolean) => boolean } }
		).Nette
		if (nette !== undefined && !nette.validateForm(form, true)) {
			return // client validation will block this submit — keep the draft
		}
		const scope = getFormScope(form)
		if (scope !== null) {
			removeDraft(scope)
		}
	})
}

export const FeedbackMessengerWindowDraft = (() => {
	const init = () => {
		if (!prunedThisPageLoad) {
			prunedThisPageLoad = true
			pruneDrafts()
		}
		restoreWindowDraft()
		bindWindowDraft()
	}

	return {
		init,
		debouncedSaveWindowDraft,
	}
})()
